import React, { useContext, forwardRef } from 'react';
import { NavContext } from 'Context/nav-context';
import clsx from 'clsx';
import Icons from '../icons';
import MobileAccountBar from './mobile-account-bar';
import { chevronRightIcon, KEY } from 'Data/constants';
import Icon from '@lds/icon';
import { useIntl } from 'react-intl';
import { A11yList } from 'Components/shared/index';
import { type CMSData } from '@/types/cmsData';
import { formatLink } from '@/utils';

//styles
import styles from './mobile-top-nav.module.scss';

interface TopNavProps {
  level: string;
  navigationMobile: CMSData[];
  setCurrentIndex: React.Dispatch<number>;
  toggleMobileNav?: () => void;
}

// TODO: fix strange behaviour when using a combination of TAB and arrow keys to traverse navigation
const TopNav = forwardRef<HTMLUListElement, TopNavProps>(({ level, navigationMobile, setCurrentIndex }, ref) => {
  const {
    category: {
      ['mobileUid']: [mobileUid, setMobileUid],
      ['L0']: [, setL0],
    },
  } = useContext(NavContext);

  const { formatMessage } = useIntl();

  const navL0 = navigationMobile?.filter((item) => {
    return item.uid === mobileUid;
  })[0];

  const onSelection = ({
    event,
    hasChildren,
    uid,
  }: {
    event: React.KeyboardEvent | React.MouseEvent;
    hasChildren: boolean | null;
    uid: string;
  }) => {
    const key = (event as React.KeyboardEvent).key || event.type;

    if (!hasChildren && key === KEY.ENTER) {
      if (event.target instanceof HTMLElement && event.target.children[0] instanceof HTMLElement) {
        event.target.children[0].click();
      }

      return;
    }

    if (hasChildren && (key === 'click' || key === KEY.ARROW_RIGHT || key === KEY.ENTER)) {
      event.preventDefault();

      document.getElementById('carouselOuterContainer')!.scrollTop = 0;

      setL0(uid);
      setCurrentIndex(1);
    }
  };

  return (
    <nav id={'hamburger__' + level} className={clsx(styles.mobileTopNavContainer)}>
      <div className={styles.navHeaderLayout}>
        <A11yList
          id="mobileMenuTopNav"
          listStyle={styles.navItemsLayout}
          ariaLabel={formatMessage({ id: 'breadcrumbsShop' })}
          skipFocus={true}
          trapFocus={true}
          forwardedRef={ref}
        >
          {navigationMobile?.map((item) => {
            return (
              <React.Fragment key={item.uid}>
                <li
                  role="presentation"
                  className={clsx(styles.navItem, item.uid === mobileUid && styles.selectedNav)}
                  key={item.uid}
                  id={item.uid}
                  tabIndex={0}
                  onKeyDown={(event) => {
                    if (
                      event.key === KEY.ENTER &&
                      event.target instanceof HTMLElement &&
                      event.target.children[0] instanceof HTMLElement
                    )
                      event.target.children[0].click();
                  }}
                >
                  <a
                    href={formatLink(item.link)}
                    onClick={() => {
                      setMobileUid(item.uid);
                    }}
                  >
                    {item.name}
                  </a>
                </li>
                {item.uid === mobileUid
                  ? navL0?.child?.map(({ name, uid, link, hasChildren }) => (
                      <li
                        role="presentation"
                        className={styles.navItemL0}
                        id={uid}
                        key={name}
                        onClick={(event) => onSelection({ event, hasChildren, uid })}
                        onKeyDown={(event) => onSelection({ event, hasChildren, uid })}
                      >
                        <a href={formatLink(link)}>
                          {name}
                          {hasChildren ? (
                            <Icon
                              classes={styles.chevronRight}
                              alt={formatMessage({ id: 'rightArrow' })}
                              src={chevronRightIcon}
                              width={8}
                              height={8}
                            />
                          ) : null}
                        </a>
                      </li>
                    ))
                  : null}
                {item.uid.includes('ShopNavNode') && <MobileAccountBar setCurrentIndex={setCurrentIndex} />}
              </React.Fragment>
            );
          })}
          <Icons />
        </A11yList>
      </div>
    </nav>
  );
});

TopNav.displayName = 'TopNav';

export default TopNav;
